import { useQuery } from '@tanstack/react-query';

import type { GetAssignOdometerReadingResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get assigned odometer reading data from a purchased vehicle
 */
export const getAssignOdometerReading = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetAssignOdometerReadingResponse> => {
	const response = await apiClient.get<GetAssignOdometerReadingResponse>(
		ordersEndpoints.assignOdometerReading(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting assigned odometer reading data from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetAssignOdometerReading = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.assignOdometerReading.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get assigned odometer reading without an order ID.');
			}
			return getAssignOdometerReading(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
